import { Plus } from "lucide-react";
import { AddOrEditMatterModal } from "@/components/features/matter/layout/AddOrEditMatterModal/AddOrEditMatterModal";
import MattersSearchBar from "@/components/features/matters/MattersSearchBar/MattersSearchBar";
import { MattersTable } from "@/components/features/matters/MattersTable/MattersTable";
import { AppPageShell, Button, Text } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useSearchObject } from "@/hooks/useSearchObject";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { REFERRAL_STATUS_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

export function ReferralsListView() {
  const { hasActionPermission } = useUser();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const {
    searchObject,
    addSearchObjectValue,
    removeSearchObjectValue,
    clearSearchObject,
  } = useSearchObject({
    syncWithSearchParams: true,
    itemType: "referral",
  });

  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.create,
  );

  return (
    <AppPageShell className="h-full min-h-0 overflow-hidden">
      <Text as="h1" variant="title" className="mb-6 shrink-0">
        All referrals
      </Text>
      <div
        id="referrals-list-view"
        className="flex min-h-0 min-w-0 flex-1 flex-col"
      >
        <div id="referrals-table" className="flex min-h-0 flex-1 flex-col">
          <MattersTable
            chrome="matterDashboard"
            entityLabelPlural="referrals"
            scrollableBody
            tableName={tanstackTableNames.referrals}
            showReferredToColumn
            statusCategories={[...REFERRAL_STATUS_CATEGORY_OPTIONS]}
            onRowClick={async (row) => {
              await navigate({ to: `/matters/${row.original.id}` });
            }}
            searchBar={
              <div id="referrals-search-bar">
                <MattersSearchBar
                  searchObject={searchObject}
                  addSearchObjectValue={addSearchObjectValue}
                  removeSearchObjectValue={removeSearchObjectValue}
                  clearSearchObject={clearSearchObject}
                  showReferredToFilter
                  statusCategories={[...REFERRAL_STATUS_CATEGORY_OPTIONS]}
                />
              </div>
            }
            searchObject={searchObject}
            tableButtons={
              hasCreatePermission ? (
                <Button
                  variant="foreground"
                  size="md"
                  onClick={() => {
                    setIsCreating(true);
                  }}
                  data-testid="create-referral-button"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Create referral
                </Button>
              ) : null
            }
          />
        </div>
      </div>

      <AddOrEditMatterModal
        open={isCreating}
        setOpen={setIsCreating}
        editing={false}
        creatingMore={false}
        allowedMatterStatusCategories={[...REFERRAL_STATUS_CATEGORY_OPTIONS]}
        afterCreateFn={(res) => navigate({ to: `/matters/${res.data.id}` })}
      />
    </AppPageShell>
  );
}
